// file-upload.component.ts
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { HttpClient, HttpEventType, HttpHeaders, HttpResponse } from '@angular/common/http';
import { finalize } from 'rxjs/operators';
import { HttpService } from '../../shared/services/http.service'; // adjust path if needed
import { Location } from "@angular/common";
import { TranslateService } from 'ng2-translate'; // <-- add this import

interface RepairItem {
  ExternalReference?: string;
  ServiceOrderId?: string;
  isOutOfWarranty?: string;
  status?: string;
  responseCode?: string;
  errorMessage?: string;
}

interface UploadResponse {
  status?: string;
  responseCode?: number;
  message?: string;
  fileUrl?: string;
  documentName?: string;
  repairList?: RepairItem[];
}

@Component({
  selector: 'app-file-upload',
  template: require('./file-upload.component.html'),
  styles: [require('./file-upload.component.scss')]
})
export class FileUploadComponent implements OnInit {
  uploadForm: FormGroup;
  selectedFile?: File | null = null;
  uploadProgress = 0;
  uploading = false;
  serverResponse?: UploadResponse | null = null;
  errorMessage?: string | null = null;

  // keep a local selectedLanguage only if you need to show it in this component (optional)
  selectedLanguage: string = 'English';

  // Use your real backend URL or relative path with proxy as needed
  private readonly uploadUrl = 'https://icbupload-dot-icbservice-dot-kitchenaid-web-return-dev.uc.r.appspot.com/createRepairsFromFile';

  // validation constants
  private readonly MAX_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB
  private readonly ALLOWED_MIMES = [
    'application/vnd.ms-excel', // .xls
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
    'text/csv', // .csv
    'application/csv',
    'text/plain'
  ];

  // headers constants (apikey/wpassId as before)
  private readonly API_KEY = 'AIzaSyDYlzt8e-7tSUzzDS802X_4RgQMmx0N79Y';
  private readonly WPASS_ID = '@devtest';

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private httpService: HttpService,
    private location: Location,
    private translate: TranslateService // <-- inject TranslateService
  ) {
    this.uploadForm = this.fb.group({ file: [null] });
  }

  ngOnInit(): void {
    // Restore the language chosen elsewhere in the app (keeps session language until page reload)
    try {
      const saved = (typeof localStorage !== 'undefined') ? localStorage.getItem('appLanguage') : null;
      if (saved) {
        this.selectedLanguage = saved;
        // ensure translate library uses the saved language
        try { this.translate.use(saved); } catch (e) { console.warn('translate.use failed', e); }
      }
    } catch (e) {
      console.warn('Language init (file upload) error', e);
    }
  }

  /**
   * Called from the <input type="file"> change event.
   * Validates file type and size, sets selectedFile or sets errorMessage.
   */
  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    this.errorMessage = null;

    if (!input.files || input.files.length === 0) {
      this.selectedFile = null;
      return;
    }

    const file = input.files[0];

    // Validate mime OR extension because some browsers don't always provide mime
    const lowerName = (file.name || '').toLowerCase();
    const hasValidExt = lowerName.endsWith('.xls') || lowerName.endsWith('.xlsx') || lowerName.endsWith('.csv');
    const hasValidMime = this.ALLOWED_MIMES.indexOf(file.type) !== -1;

    if (!hasValidExt && !hasValidMime) {
      // TODO: replace these strings with translate.instant('file_type_invalid') after adding key to JSONs
      this.errorMessage = 'Invalid file type. Allowed: .xls, .xlsx, .csv';
      this.selectedFile = null;
      try { input.value = ''; } catch (e) { /* ignore */ }
      return;
    }

    if (file.size > this.MAX_SIZE_BYTES) {
      this.errorMessage = 'File size exceeds 5 MB. Please choose a smaller file.';
      this.selectedFile = null;
      try { input.value = ''; } catch (e) { /* ignore */ }
      return;
    }

    // valid
    this.selectedFile = file;
    this.uploadForm.patchValue({ file: this.selectedFile });
  }

  /**
   * Wrapper so template (ngSubmit)="onSubmit()" works.
   */
  onSubmit() {
    this.onUpload();
  }

  back() {
    this.location.back();
  }

  /**
   * The upload flow: builds FormData, reads token from HttpService/session/localStorage,
   * posts the file (multipart/form-data) and tracks progress & response.
   */
  onUpload() {
    this.errorMessage = null;
    this.serverResponse = null;

    if (!this.selectedFile) {
      this.errorMessage = 'No file selected.';
      return;
    }

    // defensive validation again
    const name = (this.selectedFile.name || '').toLowerCase();
    if (!(name.endsWith('.xls') || name.endsWith('.xlsx') || name.endsWith('.csv'))) {
      this.errorMessage = 'Selected file is not a valid spreadsheet (xls/xlsx/csv).';
      return;
    }
    if (this.selectedFile.size > this.MAX_SIZE_BYTES) {
      this.errorMessage = 'Selected file exceeds 5 MB.';
      return;
    }

    const form = new FormData();
    form.append('file', this.selectedFile, this.selectedFile.name);

    // Get token from service/session/localStorage (safe fallbacks)
    let tokenFromSession = '';
    try {
      if (this.httpService && (this.httpService as any).token) {
        tokenFromSession = (this.httpService as any).token;
      }
      if (!tokenFromSession && this.httpService && (this.httpService as any).sessionHandler) {
        tokenFromSession = (this.httpService as any).sessionHandler.getItem('token') || '';
      }
      if (!tokenFromSession && typeof localStorage !== 'undefined') {
        tokenFromSession = localStorage.getItem('token') || '';
      }
    } catch (e) {
      console.warn('Could not read token from HttpService/sessionHandler/localStorage', e);
    }

    const headers = new HttpHeaders({
      'apikey': this.API_KEY,
      'access-token': tokenFromSession || ''
    });

    this.uploading = true;
    this.uploadProgress = 0;

    this.http.post<UploadResponse>(this.uploadUrl, form, {
      headers: headers,
      reportProgress: true,
      observe: 'events'
    }).pipe(
      finalize(() => {
        this.uploading = false;
      })
    ).subscribe({
      next: (event: any) => {
        if (event && event.type === HttpEventType.UploadProgress) {
          if (typeof event.total === 'number' && typeof event.loaded === 'number' && event.total > 0) {
            this.uploadProgress = Math.round(100 * event.loaded / event.total);
          }
        } else if (event instanceof HttpResponse) {
          this.uploadProgress = 100;
          this.serverResponse = event.body ? event.body : null;

          if (this.serverResponse) {
            const respCode = this.serverResponse.responseCode;
            const statusStr = (this.serverResponse.status !== undefined && this.serverResponse.status !== null) ? String(this.serverResponse.status) : '';
            if ((typeof respCode === 'number' && respCode >= 400) || statusStr.toLowerCase() === 'error') {
              this.errorMessage = this.serverResponse.message ? this.serverResponse.message : 'Upload returned an error status.';
            }
          } else {
            this.errorMessage = 'Upload completed but server returned an empty body.';
          }
        }
      },
      error: (err: any) => {
        console.error('Upload failed - full error object:', err);
        if (err && err.status === 0) {
          this.errorMessage = 'Network error or CORS blocked the request (status 0). Check server CORS and preflight (OPTIONS).';
          return;
        }
        if (err && err.error) {
          try {
            this.errorMessage = typeof err.error === 'string' ? err.error : JSON.stringify(err.error);
          } catch (e) {
            this.errorMessage = 'Upload failed and error body could not be parsed.';
          }
          return;
        }
        this.errorMessage = 'Upload failed: ' + (err && err.message ? err.message : 'unknown error');
      }
    });
  }

  // helper getters for template if needed
  get failedRepairs(): RepairItem[] {
    if (!this.serverResponse || !this.serverResponse.repairList) { return []; }
    return this.serverResponse.repairList.filter(function(r: RepairItem) { return !r.status || String(r.status).toLowerCase() !== 'success'; });
  }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./src/app/icb/file-upload/file-upload.component.ts